import { combineReducers, configureStore } from '@reduxjs/toolkit';
import screens from './state/screensSlice';
import experiences from './state/experienceSlice';
import flows from './state/processingFlowSlice';
import canvas from './state/canvasSlice';
import undoRedo, { undoRedoMiddleware } from './undoRedo/undoRedoSlice';

import {
    FLUSH,
    REHYDRATE,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
} from 'redux-persist';

import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import undoable from 'redux-undo';

const persistConfig = {
    key: 'ps-web-root',
    storage,
    version: 1, // Add version to handle migrations
    /* migrate must return a Promise; a plain return stalls rehydration
       and PersistGate blanks the app until the ~5s timeout */
    migrate: (state: any): Promise<any> => {
        // If state is corrupted or has wrong structure, reset it
        if (!state || typeof state !== 'object') {
            return Promise.resolve(undefined); // redux-persist then uses initial state
        }

        // Check if experiences slice has correct structure
        if (state.experiences && !state.experiences.present) {
            // Old structure, reset experiences
            state.experiences = {
                past: [],
                present: { entities: {}, ids: [] },
                future: [],
            };
        }

        return Promise.resolve(state);
    },
};

const rootReducer = combineReducers({
    experiences: undoable(experiences, { limit: 20 }),
    screens: undoable(screens, { limit: 20 }),
    flows: undoable(flows, { limit: 20 }),
    canvas: canvas, // Canvas state doesn't need undo/redo
    undoRedo: undoRedo, // Our new undo/redo system
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [
                    FLUSH,
                    REHYDRATE,
                    PAUSE,
                    PERSIST,
                    PURGE,
                    REGISTER,
                ],
            },
        }).concat(undoRedoMiddleware),
});

/* one persistor per store: a second persistStore() never rehydrates, so
   its PersistGate waits out the ~5s timeout (blank app on load) */
let persistor: ReturnType<typeof persistStore> | undefined;
const createExperienceStore = () => {
    if (!persistor) persistor = persistStore(store);

    return { store, persistor };
};

// Export the RootState type
export type RootState = ReturnType<typeof store.getState>;

export default createExperienceStore;
